import React from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { CenterTotalLabel } from 'features/charts-v2/strategies/CenterTotalLabel'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
  TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { buildBreakdownColorMap } from 'features/charts-v2/utils/breakdown-color-map'
import { getMetricLabel } from 'features/charts-v2/utils/metric-format-utils'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
`

// Pie chart data point with required properties
interface PieDataPoint extends TransformedDataPoint {
  name: string
  value: number
  fill: string
  percentage: number
}

// Slices below this share of the total skip their inside label — the arc is
// too narrow to fit text legibly. Small slices still appear in the tooltip.
const LABEL_MIN_PERCENT = 0.05

export class PieChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.PIE
  public readonly requiredMetrics: MetricType[] = [METRICS.RECORD_COUNT.id]
  public readonly optionalMetrics: MetricType[] = [METRICS.SENTENCE_COUNT.id]

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single pie slice with total
    if (breakdowns.length === 0) {
      const totalData = dataTransformationService.transformForTotalOnly(
        props.data!,
        props.selectedMetrics,
      )

      // Transform to pie chart format with percentage and color
      if (
        totalData.chartData &&
        totalData.chartData.length > 0 &&
        totalData.chartData[0]
      ) {
        const totalValue =
          (totalData.chartData[0] as { count?: number }).count || 0
        return {
          chartData: [
            {
              name: 'Total',
              value: totalValue,
              percentage: 100,
              fill: chartConfig.getColor('pie', 0),
            },
          ],
        }
      }
      return { chartData: [] }
    }

    return dataTransformationService.transformForPieChart(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      data,
      colorReferenceData,
      selectedBreakdowns,
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showDatapointLabels = true,
      hoveredDataPoint,
      disableAnimation = false,
    } = props

    const colorMap =
      props.colorMap ??
      buildBreakdownColorMap(
        selectedBreakdowns?.[0] ?? null,
        colorReferenceData ?? data,
      )

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    const pieData = transformedData.chartData

    const wrapTextToLines = (text: string, availPx: number): string[] => {
      const approxCharW = (fontSizePx - 1) * 0.55
      const maxChars = Math.max(8, Math.floor(availPx / approxCharW))
      if (text.length <= maxChars) return [text]
      const words = text.split(' ')
      const lines: string[] = []
      let cur = ''
      for (const word of words) {
        const next = cur ? `${cur} ${word}` : word
        if (next.length <= maxChars) {
          cur = next
        } else {
          lines.push(cur || word)
          cur = cur ? word : ''
          if (lines.length === 1) {
            cur = text.slice((lines[0]?.length ?? 0) + 1)
            break
          }
        }
      }
      if (cur)
        lines.push(
          cur.length > maxChars ? `${cur.slice(0, maxChars - 1)}…` : cur,
        )
      return lines.slice(0, 2)
    }

    const renderLabelLine = ({
      points,
      percent = 0,
      stroke,
    }: {
      points?: Array<{ x: number; y: number }>
      percent?: number
      stroke?: string
    }): React.ReactElement => {
      if (!points || points.length < 2 || percent < LABEL_MIN_PERCENT)
        return <g />
      const [p0, p1] = points
      return (
        <path
          d={`M${p0!.x},${p0!.y}L${p1!.x},${p1!.y}`}
          stroke={stroke}
          fill="none"
        />
      )
    }

    const renderLabel = ({
      cx = 0,
      cy = 0,
      midAngle = 0,
      innerRadius = 0,
      outerRadius = 0,
      percent = 0,
      name = '',
      value = 0,
    }: {
      cx?: number
      cy?: number
      midAngle?: number
      innerRadius?: number
      outerRadius?: number
      percent?: number
      name?: string
      value?: number
    }): React.ReactElement | null => {
      if (percent < LABEL_MIN_PERCENT) return null
      const RAD = Math.PI / 180
      const elements: React.ReactElement[] = []

      if (showDatapointLabels) {
        const midRadius = (innerRadius + outerRadius) / 2
        const ix = cx + midRadius * Math.cos(-midAngle * RAD)
        const iy = cy + midRadius * Math.sin(-midAngle * RAD)
        const LH = fontSizePx + 2
        elements.push(
          <text
            key="inside"
            x={ix}
            y={iy}
            fill="#fff"
            fontSize={fontSizePx}
            fontFamily='-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            textAnchor="middle"
            dominantBaseline="central"
            pointerEvents="none"
          >
            <tspan x={ix} dy={-(LH / 2)}>
              {value.toLocaleString()}
            </tspan>
            <tspan x={ix} dy={LH}>
              {`(${(percent * 100).toFixed(1)}%)`}
            </tspan>
          </text>,
        )
      }

      if (showXAxisLabel) {
        const cos = Math.cos(-midAngle * RAD)
        const r = outerRadius + 14
        const ox = cx + r * cos
        const oy = cy + r * Math.sin(-midAngle * RAD)
        const textAnchor = cos >= 0 ? 'start' : 'end'
        const availW = Math.max(40, (cos >= 0 ? cx * 2 - ox : ox) - 4)
        const nameLines = wrapTextToLines(name, availW)
        const LH = fontSizePx + 2
        const firstDy = -((nameLines.length - 1) / 2) * LH
        elements.push(
          <text
            key="outside"
            x={ox}
            y={oy}
            textAnchor={textAnchor}
            fill="#333"
            fontSize={fontSizePx - 1}
            fontFamily='-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            pointerEvents="none"
          >
            {nameLines.map((line, i) => (
              <tspan key={i} x={ox} dy={i === 0 ? firstDy : LH}>
                {line}
              </tspan>
            ))}
          </text>,
        )
      }

      if (elements.length === 0) return null
      return <g>{elements}</g>
    }

    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              // outerRadius={showXAxisLabel ? '80%' : '100%'}
              outerRadius="90%"
              innerRadius="50%"
              isAnimationActive={
                !showXAxisLabel && !showDatapointLabels && !disableAnimation
              }
              label={
                showXAxisLabel || showDatapointLabels ? renderLabel : false
              }
              labelLine={showXAxisLabel ? renderLabelLine : false}
              onMouseEnter={(data: PieDataPoint) => {
                onDataPointHover?.(data.name)
              }}
              onMouseLeave={() => onDataPointHover?.(null)}
              onClick={(
                data: PieDataPoint,
                _index: number,
                event: React.MouseEvent,
              ) => {
                if (onSegmentClick && data) {
                  onSegmentClick(
                    {
                      primaryBreakdown: data.name,
                      value: data.value,
                      payload: data as Record<
                        string,
                        string | number | boolean | null
                      >,
                    },
                    event,
                  )
                }
              }}
            >
              {(pieData as PieDataPoint[]).map(
                (entry: PieDataPoint, index: number) => {
                  const isHovered = hoveredDataPoint === entry.name
                  const opacity =
                    hoveredDataPoint == null
                      ? 0.8
                      : isHovered
                        ? HOVER_FOCUSED_OPACITY
                        : HOVER_DIMMED_OPACITY
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={colorMap.get(entry.name) ?? entry.fill}
                      opacity={opacity}
                      stroke={isHovered ? '#1e40af' : '#fff'}
                      strokeWidth={isHovered ? 2 : 1}
                      style={{
                        cursor: 'pointer',
                        transition: HOVER_TRANSITION_CSS,
                      }}
                    />
                  )
                },
              )}
            </Pie>
            {/* Center text showing total count */}
            <CenterTotalLabel
              total={(pieData as PieDataPoint[])
                .reduce(
                  (sum: number, item: PieDataPoint) => sum + item.value,
                  0,
                )
                .toLocaleString()}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload[0]) {
                  const data = payload[0].payload
                  const label =
                    selectedMetrics.length === 1
                      ? getMetricLabel(selectedMetrics[0]!)
                      : 'Value'

                  return (
                    <ChartDatapointHoverAnnotation
                      title={data.name}
                      rows={[
                        {
                          label,
                          value: data.value.toLocaleString(),
                          percentage: null,
                          color: null,
                        },
                        {
                          label: 'Percentage',
                          value: `${data.percentage.toFixed(1)}%`,
                          percentage: null,
                          color: null,
                        },
                      ]}
                      footer={null}
                      focusedField={null}
                    />
                  )
                }
                return null
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
    )
  }

  public getTitle(): string {
    return 'Distribution by Category'
  }
}
